import { AccordionProps, Stack, Typography } from '@mui/material';
import { ReactElement, useMemo } from 'react';
import { ResponsiveFieldGrid } from 'src/components/SidePanelTabs/SiteDocumentation/components/responsive-field-grid';
import { ShowMoreButton } from 'src/components/SidePanelTabs/SiteDocumentation/components/show-more-button';
import { SiteDocumentationAccordion } from 'src/components/SidePanelTabs/SiteDocumentation/components/site-documentation-accordion';
import { renderSubSystemField } from 'src/components/SidePanelTabs/SiteDocumentation/site-documentation.helpers';
import {
  AutoGeneratedFields,
  DocumentationFormField,
  DocumentationFormValue,
  DocumentationFormValues,
} from 'src/components/SidePanelTabs/SiteDocumentation/site-documentation.types';
import { subSystems } from 'src/components/SidePanelTabs/SiteDocumentation/sub-systems-const';

type CommonFieldProps = Parameters<typeof renderSubSystemField>[0]['props'];

type SubSystemFields = {
  header: string;
  fields: DocumentationFormField[];
}[];

interface SubSystemsSectionProps {
  expanded: boolean;
  onChange: AccordionProps['onChange'];
  shouldShowField: (field: DocumentationFormField) => boolean;
  commonProps: CommonFieldProps;
  onFieldChange: (fieldName: string, value: DocumentationFormValue) => void;
  onResetField: (fieldName: string) => void;
  autoGeneratedFields: AutoGeneratedFields;
  defaultValues: DocumentationFormValues;
  getFormValues: (fieldName: string) => DocumentationFormValue;
  dirtyFields: Record<string, unknown>;
  watchedValues: DocumentationFormValues;
  hasShowMore: boolean;
  showMore: boolean;
  hasHiddenFields: boolean;
  toggleShowMore: () => void;
}

export const SubSystemsSection = ({
  expanded,
  onChange,
  shouldShowField,
  commonProps,
  onFieldChange,
  onResetField,
  autoGeneratedFields,
  defaultValues,
  getFormValues,
  dirtyFields,
  watchedValues,
  hasShowMore,
  showMore,
  hasHiddenFields,
  toggleShowMore,
}: SubSystemsSectionProps): ReactElement | null => {
  const sections = useMemo(() => {
    const renderedSubSystems = (fields: SubSystemFields) =>
      fields
        .map((subSystem) => {
          const innerFields = subSystem.fields.filter(shouldShowField).map((field) =>
            renderSubSystemField({
              field,
              props: commonProps,
              onFieldChange,
              onResetField,
              autoGeneratedFields,
              defaultValue: defaultValues[field.name],
              getFormValues,
            }),
          );

          if (!innerFields.length) {
            return null;
          }

          return (
            <Stack key={subSystem.header} gap={0.5}>
              <Typography variant="subtitle2" fontSize="0.75em" px={1} mb={0.5}>
                {subSystem.header}
              </Typography>
              <ResponsiveFieldGrid>{innerFields}</ResponsiveFieldGrid>
            </Stack>
          );
        })
        .filter(Boolean);

    let result = renderedSubSystems(subSystems);
    // show at least one field if none are visible but additional fields exist,
    // so user still has a starting point before expanding
    if (result.length === 0 && hasHiddenFields) {
      const firstSection = subSystems[0];
      result = renderedSubSystems([
        {
          ...firstSection,
          fields: [{ ...firstSection.fields[0], alwaysVisible: true }],
        },
      ]);
    }
    return result;
    // dirtyFields & watchedValues are needed to recalculate when any field is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoGeneratedFields,
    commonProps,
    defaultValues,
    getFormValues,
    onFieldChange,
    onResetField,
    shouldShowField,
    dirtyFields,
    watchedValues,
    hasHiddenFields,
  ]);

  const hasAnySection = sections.some(Boolean);

  if (!hasAnySection) {
    return null;
  }

  return (
    <SiteDocumentationAccordion title="Sub Systems" expanded={expanded} onChange={onChange}>
      <Stack gap={1.5}>
        <Stack gap={1}>{sections}</Stack>
        {!!hasShowMore && !!hasHiddenFields && (
          <ShowMoreButton expanded={showMore} onToggle={toggleShowMore} />
        )}
      </Stack>
    </SiteDocumentationAccordion>
  );
};
